import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // ← Added
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import Cropper from "react-easy-crop";

const AddCategory = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const nameInputRef = useRef(null);

  // === Image Upload & Crop States ===
  const [rawImageUrl, setRawImageUrl] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(
    formData.imageUrl || null
  );
  const [imageFile, setImageFile] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = ["Category name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = ["Category name must contain only letters"];
    } else if (formData.name.length > 50) {
      newErrors.name = ["Category name must not exceed 50 characters"];
    }

    if (formData.description && formData.description.length > 250) {
      newErrors.description = ["Description must not exceed 250 characters"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Crop & Generate Image (185x140)
  const getCroppedImg = (imageSrc, croppedAreaPixels) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.crossOrigin = "anonymous";

      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = 185;
        canvas.height = 140;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          185,
          140
        );

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Canvas is empty"));
            const file = new File([blob], "category-cropped.png", {
              type: "image/png",
            });
            const url = URL.createObjectURL(blob);
            resolve({ file, url });
          },
          "image/png",
          1.0
        );
      };

      image.onerror = (err) => reject(err);
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showSnackbar("Please upload a valid image file", "error");
      return;
    }

    if (file.size > 4 * 1024 * 1024) {
      showSnackbar("Image size must be less than 4MB", "error");
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      if (width === 185 && height === 140) {
        setImageFile(file);
        setUploadedImageUrl(objectUrl);
        setFormData((prev) => ({ ...prev, image: file }));
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          setRawImageUrl(reader.result);
          setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
      }
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = () => {
      showSnackbar("Failed to read image dimensions", "error");
      URL.revokeObjectURL(objectUrl);
    };
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async () => {
    try {
      const { file, url } = await getCroppedImg(rawImageUrl, croppedAreaPixels);
      setImageFile(file);
      setUploadedImageUrl(url);
      setFormData((prev) => ({ ...prev, image: file }));
      setCropDialogOpen(false);
      setRawImageUrl(null);
    } catch (err) {
      showSnackbar("Failed to crop image", "error");
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setRawImageUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(setErrors);
    } catch (err) {
      if (err.response?.data?.errors?.name) {
        nameInputRef.current?.focus();
        setErrors((prev) => ({
          ...prev,
          name: err.response.data.errors.name,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      image: null,
    });
    setErrors({});
    setUploadedImageUrl(null);
    setImageFile(null);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const isValid = /^[a-zA-Z ]*$/.test(value);
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      const limitedValue = filtered.slice(0, 50);

      setFormData((prev) => ({
        ...prev,
        name: limitedValue,
      }));

      if (!limitedValue.trim()) {
        setErrors((prev) => ({
          ...prev,
          name: ["Category name is required"],
        }));
      } else if (!isValid && value !== "") {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters and spaces are allowed"],
        }));
      } else if (limitedValue.length > 50) {
        setErrors((prev) => ({
          ...prev,
          name: ["Category name must not exceed 50 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "description") {
      const limitedValue = value.slice(0, 250);

      setFormData((prev) => ({
        ...prev,
        description: limitedValue,
      }));

      if (value.length > 250) {
        setErrors((prev) => ({
          ...prev,
          description: ["Description must not exceed 250 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.description;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  return (
    <>
      {/* Main Add/Edit Category Dialog */}
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (loading) return;
          handleCancel();
        }}
        disableEscapeKeyDown={loading}
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "400px",
            maxWidth: "95vw",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: isMobile ? "1rem" : "1.25rem",
            position: "relative",
            pr: 6,
          }}
        >
          {formData.id ? "Edit Category" : "Add Category"}

          <IconButton
            aria-label="close"
            onClick={handleCancel}
            disabled={loading}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 2,
            maxHeight: isMobile ? "70vh" : "auto",
            overflowY: "auto",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
              {/* Category Name */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Category Name*
                </Typography>
                <TextField
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter category name"
                  fullWidth
                  error={!!errors.name}
                  helperText={
                    errors.name?.[0] || `${formData.name.length}/50`
                  }
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputRef={nameInputRef}
                  inputProps={{ maxLength: 50 }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    },
                  }}
                />
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Description (Optional)
                </Typography>
                <TextField
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={
                    errors.description?.[0] || `${formData.description.length}/250`
                  }
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ maxLength: 250 }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiInputBase-inputMultiline": {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                      lineHeight: 1.5,
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    },
                  }}
                />
              </Box>

              {/* Category Image Upload */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Category Image (Optional)
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    mb: 1,
                    background: Color.primary.main,
                    color: "white",
                    "&:hover": { background: Color.primary.dark },
                  }}
                >
                  Choose Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary">
                  Recommended size: 185×140 pixels (will be cropped if needed)
                </Typography>

                {/* Preview */}
                {uploadedImageUrl && (
                  <Box mt={2}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        mb: 1,
                        color: Color.label.main,
                      }}
                    >
                      Preview
                    </Typography>
                    <img
                      src={uploadedImageUrl}
                      alt="Category preview"
                      style={{
                        width: "100%",
                        maxWidth: 300,
                        height: 140,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                onClick={handleCancel}
                sx={{
                  background: "none",
                  color: Color.secondary.main,
                  border: "1px solid",
                  borderColor: Color.secondary.main,
                  width: isMobile ? "100%" : "auto",
                  height: isMobile ? "40px" : "45px",
                  fontSize: isMobile ? "0.85rem" : "1rem",
                  padding: isMobile ? "8px" : "6px 16px",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                sx={{
                  background: Color.primary.main,
                  color: "white",
                  width: isMobile ? "100%" : "auto",
                  height: isMobile ? "40px" : "45px",
                  fontSize: isMobile ? "0.85rem" : "1rem",
                  padding: isMobile ? "8px" : "6px 16px",
                  "&:hover": { background: Color.primary.dark },
                  "&.Mui-disabled": {
                    background: Color.primary.main,
                    opacity: 0.7,
                    color: "white",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : formData.id ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      {/* Crop Image Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={handleCropCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
          }}
        >
          Crop Image to 185×140

          <IconButton
            aria-label="close"
            onClick={handleCropCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ height: 400, position: "relative" }}>
          {rawImageUrl && (
            <Cropper
              image={rawImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={185 / 140}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </DialogContent>

        <Box p={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={handleCropCancel}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ background: Color.primary.main, "&:hover": { background: Color.primary.dark } }}
            onClick={handleCropConfirm}
          >
            Confirm Crop
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default AddCategory;